import { CaseDraftPayload, CaseDraftSnapShot } from "app/components/ai-chat-assistant/types";
import { useCallback, useRef, useState } from "react";

const getCaseTypeValueFromSnapshot = (snapshot: CaseDraftSnapShot): number => {
  return snapshot?.CaseDraft?.caseType?.v;
};

const extractDraftFromSnapshot = (snapshot: CaseDraftSnapShot): CaseDraftPayload | null => {
  if (!getCaseTypeValueFromSnapshot(snapshot)) {
    return null;
  }
  const draft = snapshot?.CaseDraft;
  return draft ? draft : null;
};

/**
 * Hook for managing case draft confirmation workflow.
 * Handles draft state snapshots, tracks user confirmation status, and provides methods to consume confirmed or latest drafts.
 */
// @CodeScene(disable:"Complex Method")
export function useCaseDraftConfirmation() {
  const latestDraftRef = useRef<CaseDraftPayload | null>(null);
  const previousCaseTypeRef = useRef<number | undefined>(undefined);
  const userConfirmedRef = useRef(false);
  const [canRegisterCase, setCanRegisterCase] = useState(false);

  const resetDraftConfirmation = useCallback(() => {
    latestDraftRef.current = null;
    userConfirmedRef.current = false;
    setCanRegisterCase(false);
  }, []);

  const handleStateSnapshot = useCallback((event: { type: string, snapshot?: CaseDraftSnapShot }) => {
    const snapshot = event?.snapshot;
    const draft = extractDraftFromSnapshot(snapshot);
    const caseTypeValue = getCaseTypeValueFromSnapshot(snapshot);

    if (!!caseTypeValue) {
      previousCaseTypeRef.current = caseTypeValue;
      setCanRegisterCase(Boolean(caseTypeValue));
    }

    if (!draft) {
      return;
    }

    latestDraftRef.current = draft;
    userConfirmedRef.current = !!snapshot?.UserConfirmed;
  }, []);

  const consumeConfirmedDraft = useCallback(() => {
    if (!userConfirmedRef.current || !latestDraftRef.current) {
      return null;
    }

    const draft = latestDraftRef.current;
    latestDraftRef.current = null;
    userConfirmedRef.current = false;
    return draft;
  }, []);

  const consumeLatestDraft = useCallback(() => {
    if (!latestDraftRef.current) {
      return null;
    }

    const draft = latestDraftRef.current;
    latestDraftRef.current = null;
    userConfirmedRef.current = false;
    return draft;
  }, []);

  const hasConfirmedDraft = useCallback(() => userConfirmedRef.current, []);

  return {
    resetDraftConfirmation,
    handleStateSnapshot,
    consumeConfirmedDraft,
    consumeLatestDraft,
    hasConfirmedDraft,
    canRegisterCase
  };
}
